import React, { useState } from 'react';
import { Modal, Button, ActionBar } from '@/components/common';
import type { HomeZone } from 'minka-shared';

export interface HomeZoneModalProps {
  isOpen: boolean;
  zoneId: string | null;
  currentUserId: string;
  allZones?: HomeZone[]; // All zones in the room to check if user already has one
  onClose: () => void;
  onClaimZone?: (zoneId: string) => Promise<void>;
  onUnclaimZone?: (zoneId: string) => Promise<void>;
}

export const HomeZoneModal: React.FC<HomeZoneModalProps> = ({
  isOpen,
  zoneId,
  currentUserId,
  allZones,
  onClose,
  onClaimZone,
  onUnclaimZone
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Debug logging to understand the data
  React.useEffect(() => {
    if (isOpen && zoneId) {
      console.debug(`🏠 HomeZoneModal opened for zone ${zoneId}`);
      console.debug('🏠 Available zones:', allZones?.map(z => ({ id: z.id, claimed: !!z.claimedBy })));
    }
  }, [isOpen, zoneId, allZones]);

  // Single lookup from React state - ensure we compare zone IDs as strings
  const currentZone = zoneId ? allZones?.find(z => z.id?.toString() === zoneId.toString()) : null;

  // Check if the current user already has a zone claimed
  const userHasAZone = allZones?.some(z =>
    z.claimedBy === currentUserId
  ) || false;

  const handleClaimZone = async () => {
    if (!currentZone || !onClaimZone) return;

    setIsProcessing(true);
    try {
      await onClaimZone(currentZone.id);
      onClose(); // Close modal on successful claim
    } catch (error) {
      console.error('Failed to claim zone:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnclaimZone = async () => {
    if (!currentZone || !onUnclaimZone) return;

    setIsProcessing(true);
    try {
      await onUnclaimZone(currentZone.id);
      onClose(); // Close modal on successful unclaim
    } catch (error) {
      console.error('Failed to unclaim zone:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const isOwnedByCurrentUser = currentZone?.claimedBy === currentUserId;
  const isUnclaimed = !currentZone?.claimedBy;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Home Zone" size="md">
      <div className="py-5 flex flex-col gap-4">
        {!currentZone && zoneId ? (
          // Show loading state if we have a zoneId but no zone data yet
          <div className="text-center text-gray-400">
            <p>Loading zone data...</p>
          </div>
        ) : currentZone ? (
          <>
            <div className="flex items-center justify-between gap-4">
              <label className="text-gray-400 font-semibold text-sm min-w-[80px]">Zone ID:</label>
              <div className="flex items-center gap-2 text-white font-medium text-base flex-1 justify-end">
                {currentZone.id}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <label className="text-gray-400 font-semibold text-sm min-w-[80px]">Status:</label>
              <div className="flex items-center gap-2 text-white font-medium text-base flex-1 justify-end">
                <span className={`px-2 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-full ${
                  isUnclaimed
                    ? 'bg-green-500/20 border border-green-500/30 text-green-500'
                    : 'bg-game-orange/20 border border-game-orange/30 text-game-orange'
                }`}>
                  {isUnclaimed ? 'UNCLAIMED' : 'CLAIMED'}
                </span>
              </div>
            </div>

            {!isUnclaimed && (
              <div className="flex items-center justify-between gap-4">
                <label className="text-gray-400 font-semibold text-sm min-w-[80px]">Owner:</label>
                <div className="flex items-center gap-2 text-white font-medium text-base flex-1 justify-end">
                  {currentZone.claimedByUsername || 'Unknown'}
                  {isOwnedByCurrentUser && (
                    <span className="text-game-golden text-xs font-semibold italic">(You)</span>
                  )}
                </div>
              </div>
            )}

            {currentZone.claimedAt && (
              <div className="flex items-center justify-between gap-4">
                <label className="text-gray-400 font-semibold text-sm min-w-[80px]">Claimed:</label>
                <div className="flex items-center gap-2 text-white font-medium text-base flex-1 justify-end">
                  <span className="text-sm">
                    {new Date(currentZone.claimedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <label className="text-gray-400 font-semibold text-sm min-w-[80px]">Size:</label>
              <div className="flex items-center gap-2 text-white font-medium text-base flex-1 justify-end">
                {currentZone.width && currentZone.height ? `${currentZone.width} x ${currentZone.height} px` : 'Unknown'}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400 italic">No zone data available</p>
          </div>
        )}
      </div>
      
      <ActionBar align="right" className="pt-4 border-t border-game-medium/30">
        {currentZone && isUnclaimed && onClaimZone && !userHasAZone && (
          <Button 
            variant="primary"
            onClick={handleClaimZone}
            disabled={isProcessing}
            isLoading={isProcessing}
            className="px-6"
          >
            Claim Zone
          </Button>
        )}
        {currentZone && isUnclaimed && userHasAZone && (
          <div className="text-sm text-gray-400 italic mr-auto">
            You already have a zone claimed. Unclaim it first to claim this one.
          </div>
        )}
        {currentZone && isOwnedByCurrentUser && onUnclaimZone && (
          <Button 
            variant="secondary"
            onClick={handleUnclaimZone}
            disabled={isProcessing}
            isLoading={isProcessing}
            className="px-6"
          >
            Unclaim Zone
          </Button>
        )}
        <Button 
          variant="secondary"
          onClick={onClose}
          className="px-6"
        >
          Close
        </Button>
      </ActionBar>
    </Modal>
  );
};